import React, { useId } from 'react';
import { Box, FormControl, FormHelperText, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { brand } from '../../theme';

export interface ChoiceOption<T extends string> {
  value: T;
  title: string;
  description?: React.ReactNode;
  /** A neutral outlined icon shown before the title. */
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface ChoiceCardGroupProps<T extends string> {
  /** The question the group answers; the fieldset's legend. */
  label: string;
  name: string;
  value: T | '';
  options: ChoiceOption<T>[];
  onChange: (value: T) => void;
  helperText?: React.ReactNode;
  /** Columns from the small breakpoint up (always one column on phones). */
  columns?: 2 | 3 | 4;
}

/**
 * Selectable cards with real radio semantics: a fieldset and legend, a native
 * radio in every card (Tab reaches the group, arrow keys move the choice),
 * and one selected style - a 2px copper border, no shadow.
 */
function ChoiceCardGroup<T extends string>({
  label,
  name,
  value,
  options,
  onChange,
  helperText,
  columns = 2,
}: ChoiceCardGroupProps<T>) {
  const id = useId();
  const helperId = helperText ? `${id}-helper` : undefined;

  return (
    <FormControl component="fieldset" sx={{ width: '100%', m: 0, minWidth: 0 }}>
      <FormLabel
        component="legend"
        sx={{
          mb: 1,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'text.primary',
          '&.Mui-focused': { color: 'text.primary' },
        }}
      >
        {label}
      </FormLabel>
      <RadioGroup
        name={name}
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
        aria-describedby={helperId}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: `repeat(${columns}, minmax(0, 1fr))` },
          gap: 1.5,
        }}
      >
        {options.map((option) => {
          const selected = option.value === value;
          const titleId = `${id}-${option.value}-title`;
          const descriptionId = `${id}-${option.value}-description`;
          return (
            <Box
              key={option.value}
              component="label"
              data-selected={selected ? 'true' : undefined}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                // The selected border is 1px thicker; the padding gives it back
                // so the card doesn't shift.
                p: selected ? '13px' : '14px',
                border: selected ? `2px solid ${brand.copper}` : `1px solid ${brand.borderStrong}`,
                borderRadius: 2,
                backgroundColor: option.disabled ? 'grey.100' : 'background.paper',
                cursor: option.disabled ? 'not-allowed' : 'pointer',
                transition: 'border-color 0.15s ease',
                ...(!option.disabled && !selected ? { '&:hover': { borderColor: brand.textSubtle } } : {}),
                '&:has(input:focus-visible)': { outline: `2px solid ${brand.copper}`, outlineOffset: 2 },
              }}
            >
              <Radio
                value={option.value}
                disabled={option.disabled}
                size="small"
                inputProps={{
                  'aria-labelledby': titleId,
                  'aria-describedby': option.description ? descriptionId : undefined,
                }}
                sx={{ p: 0.25, mt: '1px', flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option.icon && (
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        display: 'inline-flex',
                        color: option.disabled ? 'text.disabled' : 'text.secondary',
                        '& svg': { fontSize: 20 },
                      }}
                    >
                      {option.icon}
                    </Box>
                  )}
                  <Typography
                    id={titleId}
                    component="span"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                      lineHeight: 1.4,
                      color: option.disabled ? 'text.secondary' : 'text.primary',
                    }}
                  >
                    {option.title}
                  </Typography>
                </Box>
                {option.description && (
                  <Typography
                    id={descriptionId}
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.25 }}
                  >
                    {option.description}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </RadioGroup>
      {helperText && (
        <FormHelperText id={helperId} sx={{ mx: 0, mt: 1 }}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default ChoiceCardGroup;
